import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '@/types';
import { authApi, authUtils, ApiError } from '@/services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, inviteCode?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 初始化时检查登录状态
  useEffect(() => {
    const initAuth = async () => {
      if (authUtils.isLoggedIn()) {
        try {
          await refreshUser();
        } catch (error) {
          // token可能已过期，清除本地存储
          authUtils.clearToken();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // 登录
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const authResponse: AuthResponse = await authApi.login({ email, password });
      
      // 保存token
      authUtils.saveToken(authResponse.token);
      
      // 获取用户详细信息
      await refreshUser();
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.message);
      }
      throw new Error('登录失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 注册
  const register = async (email: string, password: string, inviteCode?: string): Promise<void> => {
    try {
      setIsLoading(true);
      const authResponse: AuthResponse = await authApi.register({ 
        email, 
        password, 
        invite_code: inviteCode 
      });
      
      // 保存token
      authUtils.saveToken(authResponse.token);
      
      // 获取用户详细信息
      await refreshUser();
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.message);
      }
      throw new Error('注册失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 登出
  const logout = (): void => {
    authUtils.clearToken();
    setUser(null);
  };

  // 刷新用户信息
  const refreshUser = async (): Promise<void> => {
    try {
      const userData: User = await authApi.me();
      setUser(userData);
    } catch (error) {
      if (error instanceof ApiError && error.code === 401) {
        // 未认证，清除token
        authUtils.clearToken();
        setUser(null);
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isLoggedIn: !!user,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook for using auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};